import React, { useMemo } from 'react';
import { toast } from 'react-hot-toast';

import { SecondaryButton } from '@components/Button/variants';
import IconPlusWithCircle from '@components/Images/Rebranding/IconPlusWithCircle';
import { useAppContext } from '@contexts/AppContext';
import useSubscribeToAllNewsletter from '@hooks/useSubscribeToAllNewsletter';
import { Newsletter } from '@utils/backend-api/newsletters';

import { NEWSLETTER_EN_LANGUAGE, SIGN_UP_FOR_ALL } from './NewsletterCard/interfaces';
import SignUpModal from './SignUpModal';

interface HeadlineProps {
  setCurrentNewsletters: React.Dispatch<React.SetStateAction<Newsletter[]>>;
  newsletters: Newsletter[];
}

const Headline = ({ setCurrentNewsletters, newsletters }: HeadlineProps) => {
  const { mutateAsync: subscribeToAllNewsletters, isError } = useSubscribeToAllNewsletter();
  const [showModal, setShowModal] = React.useState(false);
  const toggleModal = () => setShowModal((prev) => !prev);
  const { initialCurrentUser } = useAppContext();

  const handleSubscribeToAllNewsletters = async () => {
    const { newsletterIds } = await subscribeToAllNewsletters();
    if (isError) {
      toast.error('Something went wrong, please try again later');
    }
    setCurrentNewsletters((prevNewsletters: Newsletter[]) =>
      prevNewsletters.map((nl) =>
        newsletterIds.includes(Number(nl.id)) ? { ...nl, currentUserSubscribed: true } : nl
      )
    );
  };

  const shouldShowSubscribeToAllButton = useMemo(() => {
    if (!initialCurrentUser?.id) {
      return true;
    }

    const isEligibleAndNotSubscribed = (nl: Newsletter) =>
      !nl.currentUserSubscribed && nl.lang === NEWSLETTER_EN_LANGUAGE;

    if (
      newsletters.some(
        (nl) => isEligibleAndNotSubscribed(nl) && !nl.isProOnly && !nl.isSubscriberOnly
      )
    ) {
      return true;
    }

    if (
      initialCurrentUser.is_pro_subscriber &&
      newsletters.some((nl) => isEligibleAndNotSubscribed(nl) && nl.isProOnly)
    ) {
      return true;
    }

    if (
      initialCurrentUser.is_subscriber &&
      newsletters.some((nl) => isEligibleAndNotSubscribed(nl) && nl.isSubscriberOnly)
    ) {
      return true;
    }

    return false;
  }, [newsletters, initialCurrentUser]);

  return (
    <div className="mb-64 flex flex-wrap items-center justify-between">
      <div className="flex max-w-[903px] flex-col">
        <h1 className="m-0 font-suisse-screen text-headline-alt-lg">Newsletters</h1>
        <p className="mb-0 font-suisse-neue text-body-lg">
          Follow the reporters and editors who break and shape the news through The Information
          Newsletters—your one-stop shop for understanding the biggest developments and themes in
          tech. Check back soon for more newsletters launching shortly.
        </p>
      </div>
      {shouldShowSubscribeToAllButton && (
        <div className="mt-16 self-center lg:mt-0">
          <SecondaryButton
            as="button"
            onClick={initialCurrentUser?.id ? handleSubscribeToAllNewsletters : toggleModal}
            className="group rounded-[100px] !border-grayscale-gray bg-white py-6 pl-6 pr-16 hover:!bg-primary-magenta focus:bg-white focus:text-primary-magenta enabled:hover:!bg-primary-magenta"
          >
            <div className={`flex gap-8 group-hover:text-white`}>
              <IconPlusWithCircle className="fill-primary-magenta stroke-white group-hover:fill-white group-hover:stroke-primary-magenta" />
              <div className={`font-suisse-intl text-button-sm group-hover:text-white`}>
                {SIGN_UP_FOR_ALL}
              </div>
            </div>
          </SecondaryButton>
        </div>
      )}

      {showModal && (
        <SignUpModal
          description="Get the latest updates from The Information"
          onClose={toggleModal}
          subscribeToAllNewsletters
        />
      )}
    </div>
  );
};

export default Headline;
